/**
 * Email File Parser - EML format support
 * Parses EML email files and extracts headers, body, and attachments
 */

export interface Attachment {
  filename: string;
  contentType: string;
  size: number;
}

export interface EmailHeader {
  name: string;
  value: string;
  decodedValue: string;
}

export interface HeaderAnalysis {
  allHeaders: EmailHeader[];
  criticalHeaders: EmailHeader[]; // From, To, Subject, Reply-To, Return-Path
  rawHeadersString: string;
}

export interface ParsedEmail {
  headers: string;              // Raw headers for Mail Header analysis
  from: string;
  to: string[];
  cc?: string[];
  bcc?: string[];
  subject: string;
  date: string;
  htmlBody?: string;
  textBody?: string;
  attachments: Attachment[];
  headerAnalysis?: HeaderAnalysis; // NEW: For security analysis
}

/**
 * Parse EML format email file
 * EML is plain text format following RFC 822/2822
 */
export function parseEML(fileContent: string): ParsedEmail {
  // Split headers and body by first empty line
  const parts = fileContent.split(/\r?\n\r?\n/);
  const headerSection = parts[0];
  const bodySection = parts.slice(1).join('\n\n');
  
  // Parse headers into key-value pairs
  const headers = parseHeaders(headerSection);
  
  // Extract basic email metadata
  const from = headers.get('from') || '';
  const to = parseEmailList(headers.get('to') || '');
  const cc = headers.get('cc') ? parseEmailList(headers.get('cc')!) : undefined;
  const bcc = headers.get('bcc') ? parseEmailList(headers.get('bcc')!) : undefined;
  const subject = decodeHeaderValue(headers.get('subject') || '');
  const date = headers.get('date') || '';
  
  // Parse MIME content
  const contentType = headers.get('content-type') || '';
  const { htmlBody, textBody, attachments } = parseMimeContent(bodySection, contentType);
  
  return {
    headers: headerSection,
    from: decodeHeaderValue(from),
    to,
    cc,
    bcc,
    subject,
    date,
    htmlBody,
    textBody,
    attachments,
    headerAnalysis: analyzeHeaders(headers, headerSection),
  };
}

/**
 * Parse headers section into a Map
 * Handles multi-line headers (folded headers)
 */
function parseHeaders(headerSection: string): Map<string, string> {
  const headers = new Map<string, string>();
  const lines = headerSection.split(/\r?\n/);
  
  let currentHeader = '';
  let currentValue = '';
  
  for (const line of lines) {
    // Check if line starts with whitespace (continuation of previous header)
    if (line.match(/^\s/) && currentHeader) {
      currentValue += ' ' + line.trim();
    } else {
      // Save previous header if exists
      if (currentHeader) {
        headers.set(currentHeader.toLowerCase(), currentValue);
      }
      
      // Parse new header
      const colonIndex = line.indexOf(':');
      if (colonIndex > 0) {
        currentHeader = line.substring(0, colonIndex).trim();
        currentValue = line.substring(colonIndex + 1).trim();
      }
    }
  }
  
  // Save last header
  if (currentHeader) {
    headers.set(currentHeader.toLowerCase(), currentValue);
  }
  
  return headers;
}

/**
 * Parse email address list (To, CC, BCC fields)
 */
function parseEmailList(value: string): string[] {
  if (!value) return [];
  
  // Split by comma, handle quoted strings
  const emails: string[] = [];
  const parts = value.split(',');
  
  for (const part of parts) {
    const trimmed = part.trim();
    if (trimmed) {
      // Extract email from "Name <email>" format
      const match = trimmed.match(/<([^>]+)>/) || trimmed.match(/([^\s]+@[^\s]+)/);
      if (match) {
        emails.push(match[1]);
      } else {
        emails.push(trimmed);
      }
    }
  }
  
  return emails;
}

/**
 * Decode encoded header values (RFC 2047)
 * Handles =?charset?encoding?text?= format
 */
function decodeHeaderValue(value: string): string {
  // Pattern: =?charset?encoding?encoded-text?=
  const pattern = /=\?([^?]+)\?([BQ])\?([^?]+)\?=/gi;
  
  return value.replace(pattern, (_match, _charset, encoding, encodedText) => {
    try {
      if (encoding.toUpperCase() === 'B') {
        // Base64 decoding
        return atob(encodedText);
      } else if (encoding.toUpperCase() === 'Q') {
        // Quoted-printable decoding
        return decodeQuotedPrintable(encodedText);
      }
    } catch {
      // Return original if decoding fails
      return _match;
    }
    return _match;
  });
}

/**
 * Decode quoted-printable encoding
 * Handles both =XX hex encoding and soft line breaks (= at end of line)
 */
function decodeQuotedPrintable(text: string): string {
  return text
    // Remove soft line breaks (= followed by newline)
    .replace(/=\r?\n/g, '')
    // Replace underscores with spaces (in headers)
    .replace(/_/g, ' ')
    // Decode =XX hex sequences
    .replace(/=([0-9A-F]{2})/gi, (_match, hex) => {
      return String.fromCharCode(parseInt(hex, 16));
    });
}

/**
 * Parse MIME multipart content (supports nested multipart structures)
 */
function parseMimeContent(body: string, contentType: string): {
  htmlBody?: string;
  textBody?: string;
  attachments: Attachment[];
} {
  const result: { htmlBody?: string; textBody?: string; attachments: Attachment[] } = {
    attachments: [],
  };
  
  // Check if multipart
  const boundaryMatch = contentType.match(/boundary=["']?([^"';]+)["']?/i);
  
  if (boundaryMatch) {
    const boundary = boundaryMatch[1];
    const parts = body.split(new RegExp(`--${boundary.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'g'));
    
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      if (!part.trim() || part.trim() === '--') continue;
      
      const partResult = parseMimePart(part, result);
      
      if (partResult.type === 'text/html' && partResult.content) {
        result.htmlBody = partResult.content;
      } else if (partResult.type === 'text/plain' && partResult.content) {
        result.textBody = partResult.content;
      } else if (partResult.attachment) {
        result.attachments.push(partResult.attachment);
      }
      // Nested multipart results are already merged into result by parseMimePart
    }
  } else {
    // Single part message
    const lowerContentType = contentType.toLowerCase();
    if (lowerContentType.includes('text/html')) {
      result.htmlBody = body;
    } else if (lowerContentType.includes('text/plain')) {
      result.textBody = body;
    } else {
      // Default to plain text
      result.textBody = body;
    }
  }
  
  return result;
}

/**
 * Parse individual MIME part (supports nested multipart structures)
 */
function parseMimePart(part: string, parentResult?: { htmlBody?: string; textBody?: string; attachments: Attachment[] }): {
  type: string;
  content?: string;
  attachment?: Attachment;
} {
  const [headerSection, ...bodyParts] = part.split(/\r?\n\r?\n/);
  const body = bodyParts.join('\n\n');
  const headers = parseHeaders(headerSection);
  
  const contentType = headers.get('content-type') || 'text/plain';
  const contentDisposition = headers.get('content-disposition') || '';
  const contentTransferEncoding = headers.get('content-transfer-encoding') || '';
  
  // Check if this is a nested multipart
  if (contentType.toLowerCase().includes('multipart/')) {
    const nestedResult = parseMimeContent(body, contentType);
    
    // Merge nested results into parent
    if (parentResult) {
      if (nestedResult.htmlBody && !parentResult.htmlBody) {
        parentResult.htmlBody = nestedResult.htmlBody;
      }
      if (nestedResult.textBody && !parentResult.textBody) {
        parentResult.textBody = nestedResult.textBody;
      }
      parentResult.attachments.push(...nestedResult.attachments);
    }
    
    return {
      type: contentType,
      content: undefined,
    };
  }
  
  // Check if attachment
  if (contentDisposition.toLowerCase().includes('attachment')) {
    const filenameMatch = contentDisposition.match(/filename=["']?([^"';]+)["']?/i);
    const filename = filenameMatch ? filenameMatch[1] : 'attachment';
    
    return {
      type: contentType,
      attachment: {
        filename,
        contentType,
        size: body.length,
      },
    };
  }
  
  // Decode content based on transfer encoding
  let decodedContent = body;
  if (contentTransferEncoding.toLowerCase() === 'base64') {
    try {
      decodedContent = atob(body.replace(/\s/g, ''));
    } catch {
      decodedContent = body;
    }
  } else if (contentTransferEncoding.toLowerCase() === 'quoted-printable') {
    decodedContent = decodeQuotedPrintable(body);
  }
  
  return {
    type: contentType.split(';')[0].trim(),
    content: decodedContent,
  };
}

/**
 * Extract plain text from HTML content
 * Removes HTML tags and decodes entities
 */
export function extractTextFromHTML(html: string): string {
  // Remove script and style tags with their content
  let text = html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  text = text.replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '');
  
  // Remove HTML tags
  text = text.replace(/<[^>]+>/g, ' ');
  
  // Decode HTML entities
  text = text.replace(/&nbsp;/g, ' ');
  text = text.replace(/&lt;/g, '<');
  text = text.replace(/&gt;/g, '>');
  text = text.replace(/&amp;/g, '&');
  text = text.replace(/&quot;/g, '"');
  text = text.replace(/&#(\d+);/g, (_match, dec) => String.fromCharCode(dec));
  text = text.replace(/&#x([0-9A-F]+);/gi, (_match, hex) => String.fromCharCode(parseInt(hex, 16)));
  
  // Normalize whitespace
  text = text.replace(/\s+/g, ' ').trim();
  
  return text;
}

/**
 * Analyze email headers for security detection
 * Extracts all headers and identifies critical ones for invisible character analysis
 */
function analyzeHeaders(headersMap: Map<string, string>, rawHeadersString: string): HeaderAnalysis {
  const allHeaders: EmailHeader[] = [];
  const criticalHeaderNames = ['from', 'to', 'subject', 'reply-to', 'return-path', 'cc', 'bcc', 'sender'];
  const criticalHeaders: EmailHeader[] = [];

  // Convert Map to array of EmailHeader objects
  for (const [name, value] of headersMap) {
    const decodedValue = decodeHeaderValue(value);
    const header: EmailHeader = {
      name: name.charAt(0).toUpperCase() + name.slice(1),
      value,
      decodedValue,
    };
    allHeaders.push(header);

    // Track critical headers
    if (criticalHeaderNames.includes(name.toLowerCase())) {
      criticalHeaders.push(header);
    }
  }

  return {
    allHeaders,
    criticalHeaders,
    rawHeadersString,
  };
}

